import styled from 'styled-components';

import { GlareEffect } from '@sorare/core/src/atoms/animations/GlareEffect';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { NeonEdge as NeonEdgeAtom } from '@sorare/core/src/atoms/ui/NeonEdge';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import {
  desktopAndAbove,
  laptopAndAbove,
} from '@sorare/core/src/style/mediaQuery';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

export const SeriesCardPackGroupWrapper = styled.div`
  display: block;
  width: 100%;
  background:
    linear-gradient(
      180deg,
      rgba(14, 14, 14, 0.5) 0%,
      rgba(14, 14, 14, 0.9) 79.85%,
      #0e0e0e 99.69%
    ),
    var(--background-image) top left / cover no-repeat;
  border-radius: var(--double-unit);
  --border-gradient: linear-gradient(
    to bottom,
    var(--league-color) 0%,
    transparent 20%
  );
  ${borderGradientStyle};
  overflow: hidden;

  @container ${laptopAndAbove} {
    width: auto;
    background: none;
    border: none;
    border-radius: 0;
    position: relative;
    isolation: isolate;
    display: block;
    height: 100%;
  }
`;

export const Grid = styled.div`
  display: grid;
  padding-top: var(--double-unit);
  grid-template-columns: 1fr 1fr;
  width: 100%;
  gap: var(--double-unit);
  @container ${laptopAndAbove} {
    display: none;
  }
`;

export const MainPack = styled(Vertical).attrs({ gap: 2 })`
  grid-column: span 2;
  width: 100%;
  padding: var(--double-unit) 0;
  background: var(--c-nl-200);
  backdrop-filter: blur(var(--double-unit));
  border: 1px solid var(--c-nd-100);
  border-radius: var(--double-unit);
  ${borderGradientStyle};
  --border-gradient: linear-gradient(
    217.81deg,
    rgba(255, 255, 255, 0.4) 1.37%,
    rgba(252, 255, 99, 0.4) 36.66%,
    rgba(18, 14, 10, 0.4) 101.57%
  );
`;

export const SecondaryPack = styled(Vertical).attrs({ gap: 2 })`
  grid-column: span 1;
  width: 100%;
  padding: var(--double-unit);
  background: var(--c-nl-200);
  backdrop-filter: blur(var(--double-unit));
  border: 1px solid var(--c-nd-100);
  border-radius: var(--double-unit);
`;

export const SecondaryPackWrapper = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
`;

export const Header = styled.div`
  text-align: center;
  padding-bottom: var(--double-unit);
  border-bottom: 1px solid var(--c-nd-150);
  @container ${laptopAndAbove} {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

export const Logo = styled.img`
  width: 100%;
  max-width: calc(20 * var(--unit));
  height: calc(8 * var(--unit));
  object-fit: contain;
  object-position: center;
`;

export const NeonEdge = styled(NeonEdgeAtom)`
  width: 100%;
  padding: var(--double-unit);
  height: 100%;
`;

export const Desktop = styled(Vertical).attrs({ center: true })`
  display: none;
  @container ${laptopAndAbove} {
    display: flex;
    flex-grow: 1;
  }
`;

export const PacksWrapper = styled.div`
  position: relative;
  z-index: -1;
  height: 100%;
  max-width: 320px;
  width: 100%;
`;

export const PackImg = styled(GlareEffect)`
  transition: 0.6s cubic-bezier(0.03, 0.47, 0.56, 0.96);
  object-fit: contain;
  height: auto;
  width: calc(12 * var(--unit));
  @container ${desktopAndAbove} {
    width: calc(17 * var(--unit));
  }
  &:nth-child(1) {
    filter: blur(1.5px);
    position: absolute;
    left: 15%;
    @container ${desktopAndAbove} {
      left: -10%;
    }
    bottom: 75%;
    transform: translateX(-50%) translateY(50%) scale(0.85) rotate(18deg);
    ${SeriesCardPackGroupWrapper}:hover & {
      left: 9%;
      @container ${desktopAndAbove} {
        left: -16%;
      }
      transform: translateX(-50%) translateY(50%) scale(0.85) rotate(20deg);
    }
  }
  &:nth-child(2) {
    position: absolute;
    left: 50%;
    transform: translateX(-50%) translateY(50%);
    bottom: 50%;
    z-index: 1;
    ${SeriesCardPackGroupWrapper}:hover & {
      transform: translateX(-50%) translateY(50%) scale(1.1);
    }
  }
  &:nth-child(3) {
    position: absolute;
    right: 10%;
    @container ${desktopAndAbove} {
      right: 0%;
    }
    transform: translateX(50%) translateY(50%) scale(0.66) rotate(-7deg);
    bottom: 20%;
    filter: blur(1px);
    ${SeriesCardPackGroupWrapper}:hover & {
      right: 4%;
      @container ${desktopAndAbove} {
        right: -16%;
      }
      transform: translateX(50%) translateY(50%) scale(0.66) rotate(-10deg);
    }
  }
`;

export const StyledBezel = styled(Bezel)`
  transition: box-shadow 0.3s ease-in-out;
  filter: grayscale(1%);
  @container ${laptopAndAbove} {
    min-height: 265px;
  }
  @container ${desktopAndAbove} {
    min-height: 400px;
  }
  &:hover {
    box-shadow: var(--box-hover-shadow);
  }
`;
